import { useEffect, useMemo, useRef, useState } from "react";
import Table from "../../components/GTable/Table";
import { GetUsersData, GetUsersExportData } from "../../lib/data";
import Button from "../../components/GButton/Button";
import { stateOfusers, usersColumns } from "../../lib/definitions";
import { deleteUsersForm, togleBadgeForm } from "../../lib/actions";
import DeleteRecord from "../../ui/Models/deleteRecord";
import Pagination from "../../ui/paginations/Paginations";
import TableSkeleton from "../../ui/sharedUi/tableSkeleton";
import { useSearchParams } from "react-router-dom";
import { MdOutlineFileUpload } from "react-icons/md";
import UsersFilter from "../../ui/searchFilter/usersFilter";
import { useLocation } from "react-router-dom";
import i18n from "../../lib/i18n";

export default function Users() {

    const [searchParams] = useSearchParams();
    const deleteRef = useRef(null);
    const badgeRef = useRef(null);

    const [usersData, setUsersData] = useState([]);
    const [currentPage, setcurrentPage] = useState(1);
    const [last_page, setlast_page] = useState(1);
    const [deleteConfirmation, setDeleteConfirmation] = useState({ isOpen: false, idToDelete: null });
    
    // UPDATED: Added userName to the state
    const [badgeModal, setBadgeModal] = useState({ isOpen: false, userId: null, hasBadge: false, userName: "" });
    const [loadingBadge, setLoadingBadge] = useState(false);

    const [isLoading, setIsLoading] = useState(true);  
    const [loadingDelete, setLoadingDelete] = useState(false); 
    const location = useLocation(); 
    const [showExportDialog, setShowExportDialog] = useState(false);
    const [exportData, setExportData] = useState([]);
    const t = i18n.t.bind(i18n); 

    const created_at = searchParams.get('created_at');
    const name = searchParams.get('name');
    const status = searchParams.get('status');
    const email = searchParams.get('email');
    const phone = searchParams.get('phone');

    const params = useMemo(() => {
        const tempParams = [];
        if (created_at) tempParams.push({ created_at });
        if (name) tempParams.push({ name });
        if (status) tempParams.push({ status });
        if (email) tempParams.push({ email });
        if (phone) tempParams.push({ phone });

        return tempParams;
    }, [ status, created_at, name, email, phone]);

    useEffect(() => {
        handleApi();
    }, [currentPage, params])

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (deleteRef.current && !deleteRef.current.contains(event.target)) {
                closeDeleteConfirmation();
            }
            if (badgeRef.current && !badgeRef.current.contains(event.target)) {
                closeBadgeModal();
            }
        };

        if (deleteConfirmation.isOpen || badgeModal.isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [deleteConfirmation.isOpen, badgeModal.isOpen]);


    const handleApi = async () => {
        setIsLoading(true); 
        const usersData = await GetUsersData(currentPage, params);
        setUsersData([]);
        setcurrentPage(usersData.pagination.current_page);
        setlast_page(usersData.pagination.last_page);
        usersData.data.map((e) => setUsersData((prev) => [...prev, e]));
        setIsLoading(false);  
    };

    const handlePageChange = (page) => {
        setcurrentPage(page);
    };

    const openDeleteConfirmation = (id) => {
        setDeleteConfirmation({ isOpen: true, idToDelete: id });
    };

    const closeDeleteConfirmation = () => {
        setDeleteConfirmation({ isOpen: false, idToDelete: null });
    };

    const handelDelete = async () => {
        if (deleteConfirmation.idToDelete) {
            setLoadingDelete(true);
            const result = await deleteUsersForm(deleteConfirmation.idToDelete);
            if (result) {
                setLoadingDelete(false);
                handleApi();
            }
            closeDeleteConfirmation();
        }
    };

    // UPDATED: Now accepts fullName
    const openBadgeModal = (id, hasBadge, fullName) => {
        setBadgeModal({ isOpen: true, userId: id, hasBadge: hasBadge, userName: fullName });
    };

    // UPDATED: Resets userName
    const closeBadgeModal = () => {
        setBadgeModal({ isOpen: false, userId: null, hasBadge: false, userName: "" });
    };

    const handleToggleBadge = async () => {
        if (badgeModal.userId) {
            setLoadingBadge(true);
            const result = await togleBadgeForm(badgeModal.userId);
            if (result) {
                handleApi(); 
            }
            setLoadingBadge(false);
            closeBadgeModal();
        }
    };

    const openCloseExportDialog = (e) => {
        setShowExportDialog(e);
    };

    const ExportTableWithPaginiation = async (e) => {
        const exportData = await GetUsersExportData(e, params);
        setExportData([]);
        exportData.data.map((e) => setExportData((prev) => [...prev, e]));
    };

    return (
        <div className="p-8 sm:p-6 md:p-8 relative">
            {location.pathname === "/users" &&
                <div className="">
                    <UsersFilter stateOfusers={stateOfusers} values={params} />
                </div>
            }
            <div className="flex flex-col sm:flex-row sm:items-baseline border-t border-gray-500/100 bg-thirdly  justify-between px-5 py-5  gap-4">
                <div onClick={()=>openCloseExportDialog(true)}>
                    <Button background="#232424" borderRadius="8px" border="border border-gray-500/80" color="white" padding="8px 24px" text={t("button.export")} icon={<MdOutlineFileUpload />} />
                </div>
                <Button background="#D36300" borderRadius="8px" color="white" padding="8px 20px" text={t("button.add_users")} nav={"/users/add"}  />
            </div>
            <div>
                {isLoading ? <TableSkeleton cols={usersColumns}/> : (  
                    <Table 
                        name={"Users"} 
                        showExportDialog={showExportDialog} 
                        openCloseExportDialog={openCloseExportDialog} 
                        ExportTableWithPaginiation={ExportTableWithPaginiation} 
                        rowsExportPaginiation={exportData} 
                        columns={usersColumns} 
                        rows={usersData} 
                        action={"users"}
                        openDeleteConfirmation={openDeleteConfirmation} 
                        openBadgeConfirmation={openBadgeModal} 
                        edit="/users/details" 
                    />
                )}
            </div>
            <div className="flex justify-center items-center py-5">
                <Pagination currentPage={currentPage} totalPages={last_page} handleChangePage={handlePageChange} />
            </div>

            {/* Modals */}
            {deleteConfirmation.isOpen && <DeleteRecord ref={deleteRef} loadingDelete={loadingDelete} handelDelete={handelDelete} closeDeleteConfirmation={closeDeleteConfirmation} />}
            
            {/* UPDATED BADGE MODAL */}
            {badgeModal.isOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4">
                    <div ref={badgeRef} className="bg-thirdly border border-gray-600 rounded-2xl p-6 shadow-xl w-full max-w-sm text-center">
                        <h3 className="text-xl font-bold text-white mb-4">
                            {badgeModal.hasBadge 
                                ? (i18n.language === "en" ? "Remove Badge" : "إزالة الشارة") 
                                : (i18n.language === "en" ? "Grant Badge" : "منح الشارة")}
                        </h3>

                        {/* Status Box displaying Name and Current Status */}
                        <div className="mb-5 p-4 bg-[#232424] rounded-xl border border-gray-700/50 text-left">
                            <div className="flex items-center gap-2 mb-2">
                                <span className="text-gray-400 text-sm">{i18n.language === "en" ? "User:" : "المستخدم:"}</span>
                                <span className="text-white font-semibold">{badgeModal.userName}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="text-gray-400 text-sm">{i18n.language === "en" ? "Current Status:" : "الحالة الحالية:"}</span>
                                <span className={`font-semibold text-sm px-2 py-0.5 rounded ${badgeModal.hasBadge ? "bg-green-500/20 text-green-400" : "bg-gray-500/20 text-gray-300"}`}>
                                    {badgeModal.hasBadge 
                                        ? (i18n.language === "en" ? "Has Badge" : "يمتلك الشارة") 
                                        : (i18n.language === "en" ? "No Badge" : "لا يمتلك الشارة")}
                                </span>
                            </div>
                        </div>

                        <p className="text-gray-300 mb-6">
                            {badgeModal.hasBadge 
                                ? (i18n.language === "en" ? "Are you sure you want to remove the badge from this user?" : "هل أنت متأكد أنك تريد إزالة الشارة من هذا المستخدم؟")
                                : (i18n.language === "en" ? "Are you sure you want to grant a badge to this user?" : "هل أنت متأكد أنك تريد منح شارة لهذا المستخدم؟")}
                        </p>
                        
                        <div className="flex gap-4 justify-center">
                            <button 
                                onClick={closeBadgeModal} 
                                disabled={loadingBadge}
                                className="px-6 py-2 rounded-lg text-white border border-gray-500 hover:bg-gray-700 transition"
                            >
                                {i18n.language === "en" ? "Cancel" : "إلغاء"}
                            </button>
                            <button 
                                onClick={handleToggleBadge} 
                                disabled={loadingBadge}
                                className={`px-6 py-2 rounded-lg text-white transition flex items-center justify-center min-w-[100px] ${badgeModal.hasBadge ? "bg-red-500 hover:bg-red-600" : "bg-secondary hover:bg-orange-600"}`}
                            >
                                {loadingBadge ? (
                                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path></svg>
                                ) : (
                                    i18n.language === "en" ? "Confirm" : "تأكيد"
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}